import React, { useState } from 'react'
import {
    StyledRRP,
    DealerRating,
    AttentionGrabber,
    StyledDealershipIcon,
    StyledLocationRatingWrapper,
    StyledPriceAndRRPWrapper,
    StyledRatingWrapper,
    StyledSearchListingContainer,
    StyledSearchListingDetails,
    StyledSearchListingDetailsSubTitle,
    StyledSearchListingDetailsTitle,
    StyledSearchListingLabelUL,
    StyledSearchListingPrice,
    StyledSearchListingPriceLabel,
    StyledSearchListingPriceLabelWrapper,
    StyledSearchListingTop,
    StyledSpanWrap,
    StyledSpanWrapPrice,
    StyledStar,
    StyledPreRegRRP,
    StyledPreRegSaving,
    StyledPreRegSavingContainer,
    StyledSearchFinanceWrapper,
    StyledDepositAndTerms,
} from './search-listing-details.styles'
import { useATTrackInteraction } from '@at/consumer-web-snowplow'
import { StyledSearchListingLabel } from '../../patterns/search-listing/search-listing.styles'
import { VehicleLocation } from './vehicle-location/vehicle-location'
import * as Types from '../../__generated__/fed-graphql-types'
import { FinanceListingProvider, ListingType, QuoteSubType } from '../../__generated__/fed-graphql-types'
import {
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_badges_Badge,
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_dealerReview_BasicListingDealerReview,
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_finance_BasicListingFinance_disclaimerText_FinanceListingDisclaimerParagraph,
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_finance_BasicListingFinance_representativeValues_RepresentativeValues,
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_trackingContext_TrackingContext,
} from '../../contexts/search-results-context/query/__generated__/search-results-listings.fed.query'

import { FinanceModalContent } from './finance-modal-content'
import { useJourneyDataBuilder } from '../../hooks/use-journey-data-builder'
import { StyledVisuallyHiddenContainer } from '@at/design-system-foundation'
import { oneDecimalPlace } from '@at/formatter-utils/lib/number-utils/number.utils'
import { useFlexibleFinanceFilters } from '../../hooks/use-abtest.hook'

interface SearchListingDetailsProps {
    advertId: string
    financeSearch?: boolean
    fpaUrl: string
    monthlyPaymentInWholePounds?: string
    initialPayment?: string
    termMonths?: string
    price?: string | null
    quoteType?: QuoteSubType
    rrpPrice?: string | null
    saving?: string | null
    subtitle?: string | null
    title: string | null
    trackingContext?: SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_trackingContext_TrackingContext
    type: ListingType
    badges?: ReadonlyArray<SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_badges_Badge> | null
    vehicleLocation?: string | null
    location?: string | null
    formattedDistance?: string | null
    attentionGrabber?: string | null
    representativeValues?:
        | ReadonlyArray<SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_finance_BasicListingFinance_representativeValues_RepresentativeValues>
        | undefined
    disclaimerText?:
        | ReadonlyArray<SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_finance_BasicListingFinance_disclaimerText_FinanceListingDisclaimerParagraph>
        | undefined
    financeProvider?: FinanceListingProvider
    searchListingGridRef?: HTMLDivElement
    dealerReview?:
        | SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_dealerReview_BasicListingDealerReview
        | undefined
    preReg: boolean
    isBrandNew?: boolean
    sellerType?: Types.SellerType | null
    sellerName?: string | null
}

export const SearchListingDetails = ({
    advertId,
    financeSearch,
    fpaUrl,
    monthlyPaymentInWholePounds,
    initialPayment,
    termMonths,
    price,
    quoteType,
    rrpPrice,
    saving,
    subtitle,
    title,
    type,
    badges,
    attentionGrabber,
    vehicleLocation,
    representativeValues,
    trackingContext,
    disclaimerText,
    financeProvider,
    searchListingGridRef,
    dealerReview,
    preReg,
    isBrandNew,
}: SearchListingDetailsProps) => {
    const [showModal, setShowModal] = useState(false)
    const hasRRPAndSaving: boolean = !!saving && !!rrpPrice
    const handleFinanceModalInteraction = useATTrackInteraction({
        trackingLabel: 'finance-info-button',
    })

    const { journeyDataBuilder } = useJourneyDataBuilder({
        trackingContext,
        ref: searchListingGridRef,
        type,
        advertId,
    })

    const isFlexibleFinance = useFlexibleFinanceFilters()

    return (
        <StyledSearchListingContainer>
            <StyledSearchListingDetails>
                <div>
                    <StyledSearchListingTop>
                        <StyledSearchListingDetailsTitle
                            as="a"
                            data-testid="search-listing-title"
                            href={fpaUrl}
                            onClick={journeyDataBuilder}
                        >
                            {title}
                            <StyledVisuallyHiddenContainer as="span">
                                {subtitle}, {price}
                            </StyledVisuallyHiddenContainer>
                        </StyledSearchListingDetailsTitle>
                        {subtitle && (
                            <StyledSearchListingDetailsSubTitle
                                as="p"
                                data-testid="search-listing-subtitle"
                                aria-hidden={true}
                            >
                                {subtitle}
                            </StyledSearchListingDetailsSubTitle>
                        )}
                        <AttentionGrabber data-testid="search-listing-attention-grabber">
                            {attentionGrabber}
                        </AttentionGrabber>
                    </StyledSearchListingTop>
                    {badges && (
                        <StyledSearchListingLabelUL data-testid="badges-container">
                            {badges.map((badge, i) => (
                                <StyledSearchListingLabel
                                    key={`featTab-${advertId}-${i}`}
                                    as="li"
                                    data-testid={badge.type.toLowerCase()}
                                    $type={badge.type}
                                    $badges
                                >
                                    {badge.displayText}
                                </StyledSearchListingLabel>
                            ))}
                        </StyledSearchListingLabelUL>
                    )}
                </div>
                <div>
                    <StyledPriceAndRRPWrapper>
                        {preReg && hasRRPAndSaving ? (
                            <StyledPreRegSavingContainer data-testid="saving-on-rrp">
                                <StyledPreRegSaving as="span">Save {saving} </StyledPreRegSaving>
                                off
                                <StyledPreRegRRP as="span"> {rrpPrice} </StyledPreRegRRP>
                                RRP when new
                            </StyledPreRegSavingContainer>
                        ) : isBrandNew && hasRRPAndSaving ? (
                            <StyledPreRegSavingContainer data-testid="saving-on-rrp">
                                <StyledPreRegSaving as="span">Save {saving} </StyledPreRegSaving>
                                off
                                <StyledPreRegRRP as="span"> {rrpPrice} </StyledPreRegRRP>
                                RRP
                            </StyledPreRegSavingContainer>
                        ) : hasRRPAndSaving ? (
                            <StyledRRP as="span">{rrpPrice} RRP</StyledRRP>
                        ) : null}
                    </StyledPriceAndRRPWrapper>

                    <StyledSearchListingPrice
                        as="div"
                        weight={financeSearch ? '400' : '500'}
                        isFlexibleFinance={isFlexibleFinance}
                    >
                        <StyledSpanWrapPrice as="span">{price}</StyledSpanWrapPrice>
                        {isFlexibleFinance ? (
                            <StyledSearchFinanceWrapper>
                                {financeSearch && quoteType && (
                                    <StyledSearchListingPriceLabelWrapper>
                                        <StyledSearchListingPriceLabel
                                            as="button"
                                            onClick={() => {
                                                setShowModal(true)
                                                handleFinanceModalInteraction()
                                            }}
                                            id={`${advertId}-finance-info-button`}
                                            data-testid="monthly-price"
                                        >
                                            <StyledSpanWrap as="span">{monthlyPaymentInWholePounds}</StyledSpanWrap>
                                            <span style={{ fontSize: '14px' }}> / mo ({quoteType})</span>
                                        </StyledSearchListingPriceLabel>

                                        <FinanceModalContent
                                            showModal={showModal}
                                            setShowModal={setShowModal}
                                            representativeValues={representativeValues}
                                            disclaimerText={disclaimerText}
                                            financeProvider={financeProvider}
                                            financeAdvertId={`${advertId}-finance-info-button`}
                                            isFlexibleFinance={isFlexibleFinance}
                                            quoteType={quoteType}
                                        />
                                    </StyledSearchListingPriceLabelWrapper>
                                )}
                                {initialPayment && termMonths ? (
                                    <StyledDepositAndTerms>
                                        {initialPayment} deposit | {termMonths}
                                    </StyledDepositAndTerms>
                                ) : null}
                            </StyledSearchFinanceWrapper>
                        ) : (
                            financeSearch &&
                            quoteType && (
                                <StyledSearchListingPriceLabelWrapper>
                                    <StyledSearchListingPriceLabel
                                        as="button"
                                        onClick={() => {
                                            setShowModal(true)
                                            handleFinanceModalInteraction()
                                        }}
                                        id={`${advertId}-finance-info-button`}
                                        data-testid="monthly-price"
                                    >
                                        <StyledSpanWrap as="span">{monthlyPaymentInWholePounds}</StyledSpanWrap>
                                        <span style={{ fontSize: '14px' }}> / mo ({quoteType})</span>
                                    </StyledSearchListingPriceLabel>

                                    <FinanceModalContent
                                        showModal={showModal}
                                        setShowModal={setShowModal}
                                        representativeValues={representativeValues}
                                        disclaimerText={disclaimerText}
                                        financeProvider={financeProvider}
                                        financeAdvertId={`${advertId}-finance-info-button`}
                                    />
                                </StyledSearchListingPriceLabelWrapper>
                            )
                        )}
                    </StyledSearchListingPrice>

                    <StyledLocationRatingWrapper>
                        <VehicleLocation vehicleLocation={vehicleLocation} />
                        {dealerReview?.overallReviewRating ? (
                            <StyledRatingWrapper>
                                <StyledDealershipIcon title={'dealership-icon'} />
                                <DealerRating>{oneDecimalPlace(dealerReview.overallReviewRating)}</DealerRating>
                                <StyledStar title="dealer-rating" />
                            </StyledRatingWrapper>
                        ) : null}
                    </StyledLocationRatingWrapper>
                </div>
            </StyledSearchListingDetails>
        </StyledSearchListingContainer>
    )
}
